import { WorkspaceKeyPermissions } from '@robocorp/robocloud-authorizer-interface';

export type WorkspaceFlagType =
  | 'processes'
  | 'robots'
  | 'assistants'
  | 'workers'
  | 'storage'
  | 'webhooks'
  | 'vault';
export type WorkspaceKeyPermission = NonNullable<WorkspaceKeyPermissions[number]>;

interface WorkspaceKeyFlag {
  displayTitle?: string;
  title: WorkspaceKeyPermission;
  translationKey: string;
}

export interface WorkspaceFlagsByName {
  name: WorkspaceFlagType;
  items: Array<WorkspaceKeyFlag>;
}

export const WorkspaceKeys: Readonly<WorkspaceFlagsByName[]> = [
  {
    name: 'processes',
    items: [
      {
        title: 'read_processes',
        translationKey: 'readProcesses',
      },
      {
        title: 'write_processes',
        translationKey: 'writeProcesses',
      },
      {
        title: 'read_runs',
        translationKey: 'readRuns',
      },
      {
        title: 'read_run_outputs',
        translationKey: 'readRunOutputs',
      },
      {
        title: 'write_work_items',
        translationKey: 'writeWorkItems',
      },
      {
        title: 'read_work_items',
        translationKey: 'readWorkItems',
      },
      {
        title: 'trigger_processes',
        translationKey: 'triggerProcesses',
      },
    ],
  },
  {
    name: 'robots',
    items: [
      {
        title: 'read_robots',
        displayTitle: 'read_task_packages',
        translationKey: 'readRobots',
      },
      {
        title: 'write_robots',
        displayTitle: 'write_task_packages',
        translationKey: 'writeRobots',
      },
    ],
  },
  {
    name: 'assistants',
    items: [
      {
        title: 'read_assistants',
        translationKey: 'readAssistants',
      },
      {
        title: 'write_assistants',
        translationKey: 'writeAssistants',
      },
      {
        title: 'run_assistants',
        translationKey: 'runAssistants',
      },
    ],
  },
  {
    name: 'workers',
    items: [
      {
        title: 'read_environments',
        displayTitle: 'read_workers',
        translationKey: 'readWorkers',
      },
      {
        title: 'write_environments',
        displayTitle: 'write_workers',
        translationKey: 'writeWorkers',
      },
    ],
  },
  {
    name: 'storage',
    items: [
      {
        title: 'read_asset_storage_assets',
        translationKey: 'readAssetStorageAssets',
      },
      {
        title: 'write_asset_storage_assets',
        translationKey: 'writeAssetStorageAssets',
      },
    ],
  },
  {
    name: 'webhooks',
    items: [
      {
        title: 'read_webhooks',
        translationKey: 'readWebhooks',
      },
      {
        title: 'write_webhooks',
        translationKey: 'writeWebhooks',
      },
    ],
  },
  {
    name: 'vault',
    items: [
      {
        title: 'read_vault',
        displayTitle: 'read_secrets',
        translationKey: 'readSecrets',
      },
      {
        title: 'write_vault',
        displayTitle: 'write_secrets',
        translationKey: 'writeSecrets',
      },
    ],
  },
];

export type WorkspaceKeyInitialState = {
  [id in WorkspaceFlagType]: Record<string, boolean>;
};

export const workspacePermissionsInitialState: WorkspaceKeyInitialState = {
  processes: {
    read_processes: false,
    write_processes: false,
    read_runs: false,
    read_run_outputs: false,
    trigger_processes: false,
    write_work_items: false,
    read_work_items: false,
  },
  robots: {
    read_robots: false,
    write_robots: false,
  },
  assistants: {
    read_assistants: false,
    write_assistants: false,
    run_assistants: false,
  },
  workers: {
    read_environments: false,
    write_environments: false,
  },
  storage: {
    read_asset_storage_assets: false,
    write_asset_storage_assets: false,
  },
  webhooks: {
    read_webhooks: false,
    write_webhooks: false,
  },
  vault: {
    read_vault: false,
    write_vault: false,
  },
};

const internalPermissionMap: Record<string, string> = {
  read_task_packages: 'read_robots',
  write_task_packages: 'write_robots',
  read_workers: 'read_environments',
  write_workers: 'write_environments',
  read_secrets: 'read_vault',
  write_secrets: 'write_vault',
};
export const mapInternalEndpointPermissions = (permissions?: string[]) =>
  permissions?.map((permission) => internalPermissionMap[permission] || permission);
